import React from 'react';
import { Link } from 'react-router-dom';
import {
  Languages, FileText, Search, MapPin, MessageCircle, Share2, Handshake, Headphones, CheckCircle
} from 'lucide-react';
import Seo from '../../components/Seo';
import ServiceCTA from '../../components/ServiceCTA';

const services = [
  { icon: <Languages className="w-6 h-6" />, title: '日本語サイトローカライズ', en: 'Website Localization', description: '海外サイトを、日本のお客様に自然に伝わる日本語サイトへローカライズします。' },
  { icon: <FileText className="w-6 h-6" />, title: '日本語翻訳', en: 'Japanese Translation', description: '直訳ではなく、文化やニュアンスを踏まえた日本語表現に翻訳します。' },
  { icon: <Search className="w-6 h-6" />, title: '日本向けSEO', en: 'SEO for Japan', description: '日本の検索エンジンで見つけてもらえるよう、日本語SEOを最適化します。' },
  { icon: <MapPin className="w-6 h-6" />, title: 'Googleビジネスプロフィール', en: 'Google Business Profile', description: '日本国内での検索・地図表示に向けた情報整備をサポートします。' },
  { icon: <MessageCircle className="w-6 h-6" />, title: 'LINE公式アカウント開設', en: 'LINE Official Account', description: '日本で広く使われるLINEでの顧客接点づくりをお手伝いします。' },
  { icon: <Share2 className="w-6 h-6" />, title: '日本向けSNSマーケティング', en: 'SNS Marketing for Japan', description: 'Instagram・Xなど、日本の利用者に届くSNS発信を設計します。' },
  { icon: <Handshake className="w-6 h-6" />, title: '販売代理店・パートナー開拓', en: 'Distributor & Partner Outreach', description: '日本国内の販売代理店やパートナーとのつながりづくりを支援します。' },
  { icon: <Headphones className="w-6 h-6" />, title: 'カスタマーサポート戦略', en: 'Customer Support Strategy', description: '日本のお客様に安心いただける問い合わせ・サポート体制をご提案します。' },
];

const JapanMarketEntry = () => {
  return (
    <div>
      <Seo
        title="日本市場進出支援 Japan Market Entry | Sakura Local Business Support"
        description="海外企業の日本市場参入を、日本語サイトローカライズ・翻訳・日本向けSEO・Googleビジネスプロフィール・LINE公式・SNS・パートナー開拓・サポート戦略で支援します。"
        keywords="日本市場進出,Japan Market Entry,Japanese Localization,日本語翻訳,SEO for Japan,LINE Official Account,SNS Marketing,Bilingual Marketing,SLBS"
      />

      {/* Hero */}
      <section className="py-16 md:py-20" style={{ backgroundColor: '#FFF7FA' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center fade-in">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase mb-5 px-4 py-1.5 rounded-full bg-white" style={{ color: '#C77A93' }}>
              Japan Market Entry
            </span>
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#2C2C2C' }}>
              日本市場進出支援
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              海外企業が日本市場へスムーズに参入し、成長できるように。
              日本のビジネス文化への理解と現地の専門性で、参入から定着までサポートします。
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section-spacing bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#2C2C2C' }}>
              サービス内容
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              日本市場の特性に合わせて、必要なサポートを組み合わせてご提案します。
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {services.map((item, index) => (
              <div key={index} className="bg-white p-7 rounded-2xl border border-gray-100 shadow-sm hover-lift" data-testid={`japan-entry-card-${index}`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: '#FFF7FA', color: '#C77A93' }}>
                  {item.icon}
                </div>
                <p className="text-xs font-medium mb-1" style={{ color: '#C77A93' }}>{item.en}</p>
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#2C2C2C' }}>{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who is this for */}
      <section className="py-16" style={{ backgroundColor: '#FFF7FA' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-10">
            <h2 className="text-2xl font-bold mb-6" style={{ color: '#2C2C2C' }}>こんな企業様に向いています</h2>
            <ul className="space-y-3">
              {[
                '日本市場へ参入したい',
                '日本語のWebサイトをつくりたい',
                '日本のお客様にアプローチしたい',
                'マーケティングを日本向けにローカライズしたい',
                '日本国内のパートナーを見つけたい',
              ].map((item, i) => (
                <li key={i} className="flex items-start">
                  <CheckCircle className="w-5 h-5 mt-1 mr-3 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
            <Link to="/global-business" className="inline-block text-sm text-[#C77A93] hover:text-[#2C2C2C] font-medium mt-6">
              Global Business Solutions の全体像を見る →
            </Link>
          </div>
        </div>
      </section>

      <ServiceCTA
        title="日本市場への進出について、お気軽にご相談ください。"
        description="We support your entry into the Japanese market. Feel free to contact us in English or Japanese."
        buttonText="無料相談はこちら"
      />
    </div>
  );
};

export default JapanMarketEntry;
